import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faAngleDown } from "@fortawesome/pro-solid-svg-icons/faAngleDown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Dropdown from "antd/es/dropdown";
import Menu from "antd/es/menu";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { useIntl } from "react-intl";

import FieldCategory from "@mapmycustomers/shared/enum/fieldModel/FieldCategory";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import { EntityTypesSupportedByMapsPage } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { Button } from "@mapmycustomers/ui";

import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { getFieldCategoryDisplayName } from "@app/util/ui";

import messages from "../../messages";
import { selectField } from "../../store/actions";
import { getEntityType, getField } from "../../store/selectors";

import styles from "./FieldSelector.module.scss";

interface Props {
  entityType: EntityTypesSupportedByMapsPage;
  field?: IField;
  selectField: typeof selectField;
}

const FieldSelector: React.FC<Props> = ({ entityType, field, selectField }) => {
  const intl = useIntl();
  const analyticIssuer = useAnalytics();

  const [visible, setVisibility] = useState<boolean>(false);

  const categorizedFields = useMemo(() => {
    const categories = getFieldModelByEntityType(entityType).categorizedFields;
    return Object.keys(categories).reduce((result, categoryKey) => {
      const fields = categories[categoryKey as FieldCategory].filter(
        (field) => field.hasFeature(FieldFeature.DATA_VIEW) && field.isReadable
      );
      return {
        ...result,
        ...(fields.length ? { [categoryKey as FieldCategory]: fields } : {}),
      };
    }, {} as Record<FieldCategory, IField[]>);
  }, [entityType]);

  const handleSetField = useCallback(
    (field) => {
      selectField(field.name);
      setVisibility(false);
      analyticIssuer.clicked(["Change Data Source"], { field: field.displayName });
    },
    [analyticIssuer, selectField, setVisibility]
  );

  if (!field) {
    return null;
  }

  return (
    <Dropdown
      onVisibleChange={setVisibility}
      overlay={
        <Menu className={styles.dropdown}>
          {Object.keys(categorizedFields).map((key) => (
            <Menu.ItemGroup
              key={key}
              title={getFieldCategoryDisplayName(intl, key as FieldCategory)}
            >
              {categorizedFields[key as FieldCategory].map((field) => (
                <Menu.Item key={field.name} onClick={() => handleSetField(field)}>
                  {field.displayName}
                </Menu.Item>
              ))}
            </Menu.ItemGroup>
          ))}
        </Menu>
      }
      placement="bottomLeft"
      trigger={["click"]}
      visible={visible}
    >
      <Tooltip placement="top" title={intl.formatMessage(messages.changeDataSource)}>
        <Button
          className={cn(styles.selector, { [styles.selectorPressed]: visible })}
          rightIcon={<FontAwesomeIcon className={styles.fieldNameIcon} icon={faAngleDown} />}
          type="link"
        >
          <div className={styles.fieldName}>{field.displayName}</div>
        </Button>
      </Tooltip>
    </Dropdown>
  );
};

const mapStateToProps = (state: RootState) => ({
  entityType: getEntityType(state),
  field: getField(state),
});

const mapDispatchToProps = {
  selectField,
};

export default connect(mapStateToProps, mapDispatchToProps)(FieldSelector);
